import { ReloadOutlined, RollbackOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "../index.module.scss";
import css from "./refunds.module.scss";
import { useRememberedTab } from "../remembered_tab";
import {
  RefundDestination,
  RefundSource,
  RefundStage,
  RaisedByOption,
  RefundTrackerRow,
  RefundTrackerSummary,
  fetchRefundTracker,
} from "./refunds_tracker.api";

const PAGE_SIZE = 25;

const TAB_KEYS = [
  "pending",
  "processing",
  "awaiting_payout",
  "in_wallet",
  "paid",
  "closed",
  "all",
] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_STAGES: Record<TabKey, RefundStage[]> = {
  pending: [],
  processing: ["processing", "failed"],
  awaiting_payout: ["awaiting_payout", "partially_paid"],
  in_wallet: ["in_wallet"],
  paid: ["paid"],
  closed: ["cancelled", "payout_failed"],
  all: [],
};

const TAB_LABELS: Record<TabKey, string> = {
  pending: "Pending",
  processing: "Credit note in progress",
  awaiting_payout: "Awaiting bank payout",
  in_wallet: "In wallet",
  paid: "Paid to bank",
  closed: "Cancelled / failed",
  all: "All",
};

const STAGE_COLORS: Record<RefundStage, string> = {
  processing: "blue",
  failed: "red",
  awaiting_payout: "orange",
  partially_paid: "gold",
  in_wallet: "green",
  paid: "green",
  payout_failed: "volcano",
  cancelled: "default",
};

const SOURCE_OPTIONS: { label: string; value: RefundSource }[] = [
  { label: "Cancelled items", value: "deallocation" },
  { label: "Return", value: "return" },
  { label: "Damage", value: "damage" },
  { label: "Manual credit note", value: "manual_cn" },
];

const SOURCE_COLORS: Record<RefundSource, string> = {
  deallocation: "purple",
  return: "cyan",
  damage: "magenta",
  manual_cn: "geekblue",
};

const DESTINATION_OPTIONS: { label: string; value: RefundDestination }[] = [
  { label: "Wallet", value: "wallet" },
  { label: "Bank", value: "bank" },
];

const CARD_GROUPS: { label: string; primary: boolean; tabs: TabKey[] }[] = [
  { label: "Needs action", primary: true, tabs: ["pending", "processing", "awaiting_payout"] },
  { label: "Closed", primary: false, tabs: ["in_wallet", "paid", "closed", "all"] },
];

const TAB_ACCENTS: Record<TabKey, { accent: string; bg: string }> = {
  pending: { accent: "#fa8c16", bg: "#fff7e6" },
  processing: { accent: "#1677ff", bg: "#e6f4ff" },
  awaiting_payout: { accent: "#d4380d", bg: "#fff2e8" },
  in_wallet: { accent: "#389e0d", bg: "#f6ffed" },
  paid: { accent: "#08979c", bg: "#e6fffb" },
  closed: { accent: "#8c8c8c", bg: "#fafafa" },
  all: { accent: "#24aab7", bg: "#e6f7f8" },
};

const DATE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  { label: "Last 7 days", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
];

const inr = (rupees: string | number) =>
  `₹${Number(rupees).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const tabAmount = (tab: TabKey, summary: RefundTrackerSummary | null) => {
  if (!summary) return null;
  if (tab === "all") return summary.total_rupees;
  if (tab === "pending") return summary.pending_rupees;
  return TAB_STAGES[tab].reduce((n, s) => n + Number(summary.stage_rupees?.[s] ?? 0), 0);
};

const tabCount = (tab: TabKey, summary: RefundTrackerSummary | null) => {
  if (!summary) return null;
  if (tab === "all") return summary.total;
  if (tab === "pending") return summary.pending;
  return TAB_STAGES[tab].reduce((n, s) => n + (summary.stages[s] ?? 0), 0);
};

const formatDate = (v: string | null) => (v ? dayjs(v).format("DD MMM YY, HH:mm") : null);

const OmsRefundsDashboard: React.FC = () => {
  const [tab, setTab] = useRememberedTab<TabKey>("oms.refunds.tab", TAB_KEYS, "pending");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sources, setSources] = useState<RefundSource[]>([]);
  const [destinations, setDestinations] = useState<RefundDestination[]>([]);
  const [raisedBy, setRaisedBy] = useState<string[]>([]);
  const [raisedByOptions, setRaisedByOptions] = useState<RaisedByOption[]>([]);
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<RefundTrackerRow[]>([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState<RefundTrackerSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await fetchRefundTracker({
      search,
      source: sources,
      destination: destinations,
      raised_by: raisedBy,
      stage: TAB_STAGES[tab],
      pending: tab === "pending",
      date_from: dates?.[0]?.format("YYYY-MM-DD"),
      date_to: dates?.[1]?.format("YYYY-MM-DD"),
      page,
      page_size: PAGE_SIZE,
    });
    setRows(resp?.results ?? []);
    setCount(resp?.count ?? 0);
    setSummary(resp?.summary ?? null);
    if (resp) setRaisedByOptions(resp.raised_by_options ?? []);
    setLoading(false);
  }, [search, sources, destinations, raisedBy, tab, dates, page]);

  useEffect(() => {
    load();
  }, [load]);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setPage(1);
    setter(v);
  };

  const hasFilters = !!(search || sources.length || destinations.length || raisedBy.length || dates);

  const clearFilters = () => {
    setPage(1);
    setSearchInput("");
    setSearch("");
    setSources([]);
    setDestinations([]);
    setRaisedBy([]);
    setDates(null);
  };

  const columns: ColumnsType<RefundTrackerRow> = useMemo(
    () => [
      {
        title: "Raised",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        render: (v: string, row) => (
          <Space direction="vertical" size={0}>
            <span>{formatDate(v)}</span>
            {row.requested_by && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                by {row.requested_by}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Customer",
        key: "customer",
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <span>{row.customer_name || "—"}</span>
            {row.customer_contact && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.customer_contact}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Order",
        key: "order",
        render: (_, row) =>
          row.sales_order_id ? (
            <Space direction="vertical" size={0}>
              <a href={`/oms/${row.sales_order_id}`} target="_blank" rel="noreferrer">
                {row.sales_order_number}
              </a>
              {row.lead_id && row.lead_id !== row.sales_order_number && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.lead_id}
                </Typography.Text>
              )}
            </Space>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: "Type",
        dataIndex: "source",
        key: "source",
        render: (source: RefundSource, row) => (
          <Tag color={SOURCE_COLORS[source]}>{row.source_label}</Tag>
        ),
      },
      {
        title: "What's refunded",
        key: "items",
        width: 260,
        render: (_, row) => {
          const parts = [
            ...(row.items ?? []).map((i) => `${i.name || "Item"}${i.qty ? ` × ${Number(i.qty)}` : ""}`),
            ...(row.charges ?? []),
          ];
          if (!parts.length) return <Typography.Text type="secondary">—</Typography.Text>;
          return (
            <Tooltip title={parts.join(", ")}>
              <Typography.Text ellipsis style={{ maxWidth: 240 }}>
                {parts[0]}
              </Typography.Text>
              {parts.length > 1 && (
                <Typography.Text type="secondary"> +{parts.length - 1} more</Typography.Text>
              )}
            </Tooltip>
          );
        },
      },
      {
        title: "Refund to",
        dataIndex: "destination",
        key: "destination",
        render: (d: RefundDestination, row) => (
          <Space direction="vertical" size={0}>
            <Tag color={d === "bank" ? "orange" : "green"}>{d === "bank" ? "Bank" : "Wallet"}</Tag>
            {row.moved_from_wallet && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                moved from wallet
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Amount",
        key: "amount",
        align: "right",
        render: (_, row) => (
          <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
            <strong>{inr(row.amount_rupees)}</strong>
            {Number(row.outstanding_paise) > 0 && (
              <Typography.Text type="warning" style={{ fontSize: 12 }}>
                {inr(row.outstanding_rupees)} to pay
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "stage",
        key: "stage",
        render: (stage: RefundStage, row) => (
          <Tag color={STAGE_COLORS[stage]}>{row.stage_label}</Tag>
        ),
      },
      {
        title: "Credit note",
        dataIndex: "cn_number",
        key: "cn_number",
        render: (v: string | null, row) =>
          v || row.zoho_creditnote_id || <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: "UTR",
        key: "utr",
        render: (_, row) => {
          const utrs = (row.payouts ?? []).map((p) => p.utr).filter(Boolean) as string[];
          if (!utrs.length) return <Typography.Text type="secondary">—</Typography.Text>;
          return (
            <Space direction="vertical" size={0}>
              {utrs.map((u) => (
                <Typography.Text key={u} copyable style={{ fontSize: 12 }}>
                  {u}
                </Typography.Text>
              ))}
            </Space>
          );
        },
      },
    ],
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <RollbackOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>OMS — Refunds</h2>
      </div>

      <div className={css.groups}>
        {CARD_GROUPS.map((group) => (
          <div
            key={group.label}
            className={`${css.group} ${group.primary ? css.groupPrimary : css.groupSecondary}`}
          >
            <span className={css.groupLabel}>{group.label}</span>
            <div className={css.cards}>
              {group.tabs.map((k) => {
                const n = tabCount(k, summary);
                const amount = tabAmount(k, summary);
                const { accent, bg } = TAB_ACCENTS[k];
                return (
                  <button
                    key={k}
                    type="button"
                    className={`${css.card} ${tab === k ? css.cardActive : ""}`}
                    style={{ "--accent": accent, "--accent-bg": bg } as React.CSSProperties}
                    aria-pressed={tab === k}
                    onClick={() => resetPage(setTab)(k)}
                  >
                    <span className={css.cardLabel}>{TAB_LABELS[k]}</span>
                    <span className={css.cardCount}>{n === null ? "–" : n.toLocaleString("en-IN")}</span>
                    <span className={css.cardAmount}>{amount === null ? "\u00a0" : inr(amount)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className={css.filterBar}>
        <Input.Search
          allowClear
          className={css.filterSearch}
          placeholder="Search lead, SO, customer, phone, credit note"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (!e.target.value) resetPage(setSearch)("");
          }}
          onSearch={(v) => resetPage(setSearch)(v.trim())}
        />
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          placeholder="All refund types"
          style={{ minWidth: 190 }}
          value={sources}
          onChange={resetPage(setSources)}
          options={SOURCE_OPTIONS.map((o) => ({
            ...o,
            label: summary ? `${o.label} (${summary.sources[o.value] ?? 0})` : o.label,
          }))}
        />
        <Select
          allowClear
          placeholder="Wallet or bank"
          style={{ minWidth: 150 }}
          value={destinations[0]}
          onChange={(v?: RefundDestination) => resetPage(setDestinations)(v ? [v] : [])}
          options={DESTINATION_OPTIONS}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="search"
          maxTagCount="responsive"
          placeholder="Raised by anyone"
          style={{ minWidth: 190 }}
          value={raisedBy}
          onChange={resetPage(setRaisedBy)}
          options={raisedByOptions.map((o) => ({
            value: o.id,
            search: o.name,
            label: `${o.name} (${o.count})`,
          }))}
        />
        <DatePicker.RangePicker
          value={dates}
          onChange={(v) => resetPage(setDates)(v as [Dayjs | null, Dayjs | null] | null)}
          format="DD MMM YY"
          presets={DATE_PRESETS}
        />
        <div className={css.filterMeta}>
          {hasFilters && (
            <Button type="link" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
          </Tooltip>
        </div>
      </div>

      <Table<RefundTrackerRow>
        rowKey="key"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: (total) => `${total.toLocaleString("en-IN")} refunds · ${TAB_LABELS[tab]}`,
        }}
        expandable={{ expandedRowRender: (row) => <RefundDetail row={row} /> }}
        locale={{ emptyText: tab === "pending" ? "No refunds pending right now." : "No refunds found." }}
      />
    </div>
  );
};

const RefundDetail: React.FC<{ row: RefundTrackerRow }> = ({ row }) => (
  <Row gutter={24}>
    <Col xs={24} md={14}>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Items">
          {row.items?.length ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {row.items.map((i, idx) => (
                <li key={idx}>
                  {i.name || "Item"}
                  {i.qty ? ` × ${Number(i.qty)}` : ""}
                </li>
              ))}
            </ul>
          ) : (
            "—"
          )}
        </Descriptions.Item>
        {(row.charges?.length ?? 0) > 0 && (
          <Descriptions.Item label="Charges">{row.charges.join(", ")}</Descriptions.Item>
        )}
        <Descriptions.Item label="Amount">{inr(row.amount_rupees)}</Descriptions.Item>
        {row.destination === "bank" && (
          <Descriptions.Item label="Paid / to pay">
            ₹{row.paid_rupees} paid · {inr(row.outstanding_rupees)} to pay
          </Descriptions.Item>
        )}
        {(row.payouts?.length ?? 0) > 0 && (
          <Descriptions.Item label="Payouts">
            {row.payouts!.map((p, idx) => (
              <div key={p.zoho_refund_id || idx}>
                {inr(p.amount_rupees)}
                {p.paid_on ? ` on ${dayjs(p.paid_on).format("DD MMM YY")}` : ""}
                {" · UTR "}
                {p.utr ? <Typography.Text copyable>{p.utr}</Typography.Text> : "—"}
                {p.paid_from ? ` · from ${p.paid_from}` : ""}
              </div>
            ))}
          </Descriptions.Item>
        )}
        {row.reason_code && <Descriptions.Item label="Reason">{row.reason_code}</Descriptions.Item>}
        {row.notes && <Descriptions.Item label="Notes">{row.notes}</Descriptions.Item>}
        {row.escalation_id && (
          <Descriptions.Item label="Escalation">#{row.escalation_id}</Descriptions.Item>
        )}
        {row.return_escalation_id && (
          <Descriptions.Item label="Return">#{row.return_escalation_id}</Descriptions.Item>
        )}
      </Descriptions>
    </Col>
    <Col xs={24} md={10}>
      <Typography.Text strong>Progress</Typography.Text>
      <Timeline
        style={{ marginTop: 12 }}
        items={(row.timeline ?? []).map((e) => ({
          children: (
            <>
              <div>{e.label}</div>
              {e.at && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDate(e.at)}
                </Typography.Text>
              )}
            </>
          ),
        }))}
      />
    </Col>
  </Row>
);

export default OmsRefundsDashboard;
